import { Box, Button, Flex, Grid, Icon, Text, useDisclosure } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiChevronDown } from "react-icons/fi";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import type { NegotiationStage } from "./negotiationStages";
import { useTimelineDate } from "./useTimelineDate";

interface Props {
  stages: NegotiationStage[];
  roundCount: number;
}

const DASH = "–";

const HeaderCell = ({ children }: { children: string }) => (
  <Text fontSize="2xs" textTransform="uppercase" letterSpacing="wider" color="smMuted">
    {children}
  </Text>
);

// t('Negotiation {{roundNumber}}')
const StageRow = ({ stage, isCurrent }: { stage: NegotiationStage; isCurrent: boolean }) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const timelineDate = useTimelineDate();
  const color = isCurrent ? "smPrimary" : "smMuted";

  return (
    <>
      <Text fontSize="xs" color={color} fontWeight={isCurrent ? "semibold" : "normal"} whiteSpace="nowrap">
        {stage.roundNumber == null
          ? t("Original bid deadline")
          : t("Negotiation {{roundNumber}}", { roundNumber: stage.roundNumber })}
      </Text>
      <Text
        fontSize="xs"
        color={color}
        fontWeight={isCurrent ? "semibold" : "normal"}
        textAlign="right"
      >
        {displayDateWithTime(stage.deadline)}
      </Text>
      <Text fontSize="xs" color={color} textAlign="right">
        {stage.evaluation ? timelineDate(stage.evaluation) : DASH}
        {stage.evaluation?.planned && (
          <Text as="span" color="smMuted" ml="1">
            *
          </Text>
        )}
      </Text>
    </>
  );
};

/**
 * The round count, sitting in a rule that spans the tilbudsfrist and evaluering steps — those two
 * are one phase once a competition negotiates. The track shows the stage the run reached; every
 * stage behind it, and the evaluation that followed each, lives in here.
 */
export const NegotiationRoundsBadge = ({ stages, roundCount }: Props) => {
  const { t } = useTranslation();
  const { isOpen, onToggle } = useDisclosure();
  const anyPlanned = stages.some((stage) => stage.evaluation?.planned);

  return (
    <Flex direction="column" align="center" position="relative" mt="3">
      {/* The rule the badge sits in — the pair reads as one phase, not two neighbours. */}
      <Box
        display={{ base: "none", md: "block" }}
        position="absolute"
        top="3"
        left="8%"
        right="8%"
        height="1px"
        bg="smTealShadow"
      />
      <Button
        variant="unstyled"
        onClick={onToggle}
        aria-expanded={isOpen}
        height="auto"
        minW="0"
        display="inline-flex"
        alignItems="center"
        gap="1.5"
        px="2.5"
        py="1"
        borderRadius="full"
        borderWidth="1px"
        borderColor="smTealShadow"
        bg="smPrimarySofter"
        color="smPrimary"
        fontSize="xs"
        fontWeight="semibold"
        whiteSpace="nowrap"
        zIndex={1}
        _hover={{ borderColor: "smPrimary" }}
      >
        {t("{{count}} negotiation", { count: roundCount })}
        <Icon
          as={FiChevronDown}
          boxSize="3"
          transform={isOpen ? "rotate(180deg)" : undefined}
          transition="transform 140ms ease"
        />
      </Button>

      {/* Rendered only while open, rather than animated: a closed disclosure must not leave its
          dates in the DOM for a screen reader or find-in-page, and a height animation measures the
          panel before its labels have wrapped, which clips the last row. */}
      {isOpen && (
        <Box
          mt="2"
          bg="smBackground"
          borderWidth="1px"
          borderColor="smBorder"
          borderRadius="md"
          px="3"
          py="2.5"
          boxShadow="sm"
          textAlign="left"
        >
          <Grid templateColumns="auto auto auto" columnGap="5" rowGap="1" alignItems="baseline">
            <HeaderCell>{t("Stage")}</HeaderCell>
            <HeaderCell>{t("Deadline")}</HeaderCell>
            <HeaderCell>{t("Evaluation")}</HeaderCell>
            {stages.map((stage, index) => (
              <StageRow key={stage.id} stage={stage} isCurrent={index === stages.length - 1} />
            ))}
          </Grid>
          {anyPlanned && (
            <Text fontSize="2xs" color="smMuted" mt="2">
              {t("* Planned date")}
            </Text>
          )}
        </Box>
      )}
    </Flex>
  );
};
